import React, { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from '@/utils/axios';
import { showSuccessToast } from '@/components/toast/globalToast';
import { storage, getUserData } from '@/utils/storage';

const debugAuth = () => {
  const token = storage.getItem('token');
  const userData = getUserData();
  console.log('token:', token ? token.substring(0, 20) + '...' : 'None');
  console.log('profile:', userData);
};

if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuth;
}

const Index: React.FC = () => {
  const { user, token, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const hasShownWelcome = useRef(false);

  const checkExistingAuthEarly = () => {
    try {
      const existingToken = storage.getItem('token');
      const userDataString = storage.getItem('profile');

      if (existingToken && userDataString) {
        const existingUser = JSON.parse(userDataString);
        if (existingUser && existingUser.email && existingUser.uid) {
          axios.defaults.headers.common['Authorization'] =
            `Bearer ${existingToken}`;
          axios.defaults.headers.common['dash-client-type'] = 'web';
          navigate('/oems', { replace: true });
          return true;
        }
      }
    } catch (error) {
      console.warn('Failed to parse early auth check:', error);
    }
    return false;
  };

  const earlyRedirectRef = useRef(false);
  if (!earlyRedirectRef.current) {
    if (process.env.NODE_ENV === 'development') {
      debugAuth();
    }
    earlyRedirectRef.current = checkExistingAuthEarly();
  }

  useEffect(() => {
    const checkExistingAuth = () => {
      try {
        const existingToken = storage.getItem('token');
        const userDataString = storage.getItem('profile');

        if (existingToken && userDataString) {
          const existingUser = JSON.parse(userDataString);
          if (existingUser && existingUser.email && existingUser.uid) {
            axios.defaults.headers.common['Authorization'] =
              `Bearer ${existingToken}`;
            axios.defaults.headers.common['dash-client-type'] = 'web';
            navigate('/oems', { replace: true });
          }
        }
      } catch (error) {
        console.warn('Failed to parse user data from storage:', error);
      }
    };

    if (!earlyRedirectRef.current && !user && !token) {
      checkExistingAuth();
    }
  }, [navigate, user, token]);

  useEffect(() => {
    if (user && token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.defaults.headers.common['dash-client-type'] = 'web';

      if (!hasShownWelcome.current) {
        showSuccessToast(
          'Berhasil masuk!',
          'Selamat datang di Dash Fleet Management'
        );
        hasShownWelcome.current = true;
      }

      navigate('/oems', { replace: true });
    }
  }, [user, token, navigate]);

  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      await signInWithGoogle();
    } catch (err) {}
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="p-6">
        <div className="flex items-center justify-between">
          <img
            src="/images/logo.png"
            width={120}
            height={32}
            alt="Dash Electric logo"
            className="h-8 w-auto"
          />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-[520px] h-[320px] border border-2 border-grey-600 rounded-3xl p-6">
          <div className="flex justify-center mb-2">
            <div className="w-[96px] h-[96px] bg-purple-100 rounded-full flex items-center justify-center">
              <img
                src="/images/signin-image.svg"
                alt="User icon"
                className="w-[96px] h-[96px]"
              />
            </div>
          </div>

          <div className="text-center mb-4">
            <h1 className="text-[24px] font-semibold text-gray-900 mb-2">
              Login to your account
            </h1>
            <p className="text-[16px] text-gray-600">
              Enter your details to login.
            </p>
          </div>

          <div className="text-center mb-4">
            <p className="text-[14px] text-gray-500">
              Only available for @dashelectric.co accounts.
            </p>
          </div>

          <div className="mb-4">
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            >
              <img
                src="/images/google-icon.png"
                alt="Google"
                className="w-5 h-5 mr-3"
              />
              <span className="text-gray-700 font-medium">
                Login with Google
              </span>
            </button>
          </div>
        </div>
      </div>

      <footer className="p-6">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>© 2025 Dash Electric. All rights reserved</span>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              className="hover:text-gray-700 transition-colors"
              aria-label="Instagram"
              onClick={() =>
                window.open(
                  'https://www.instagram.com/dashelectric.co',
                  '_blank'
                )
              }
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </button>
            <button
              type="button"
              className="hover:text-gray-700 transition-colors"
              aria-label="LinkedIn"
              onClick={() =>
                window.open(
                  'https://www.linkedin.com/company/dash-electric/',
                  '_blank'
                )
              }
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
